import React, { useState, useEffect } from 'react';
import { useAuth } from '../contexts/AuthContext';
import { apiService } from '../services/api';
import { toast } from 'react-hot-toast';
import {
  BookOpen,
  Search,
  Bookmark,
  Clock,
  Star,
  Users,
  Calendar,
  MessageCircle,
  Edit,
  Trash2,
  Plus,
  X
} from 'lucide-react';

interface SavedFeed {
  id: number;
  userId: number;
  username: string;
  userSkillId: number;
  skillName: string;
  skillDescription: string;
  skillCategory: string;
  teacherName: string;
  teacherUsername: string;
  hourlyRate: number;
  proficiencyLevel: number;
  proficiencyDescription: string;
  availability: string;
  relationType: string;
  savedAt: string;
  notes: string;
}

const SavedFeeds: React.FC = () => {
  const { user } = useAuth();
  const [savedFeeds, setSavedFeeds] = useState<SavedFeed[]>([]);
  const [loading, setLoading] = useState(false);
  const [searchQuery, setSearchQuery] = useState('');
  const [editingNotes, setEditingNotes] = useState<{ id: number; notes: string } | null>(null);

  useEffect(() => {
    loadSavedFeeds();
  }, []);

  useEffect(() => {
    if (searchQuery) {
      const timeoutId = setTimeout(() => {
        loadSavedFeeds();
      }, 500);
      return () => clearTimeout(timeoutId);
    } else {
      loadSavedFeeds();
    }
  }, [searchQuery]);

  const loadSavedFeeds = async () => {
    try {
      setLoading(true);
      const response = await apiService.getSavedFeeds(searchQuery);
      setSavedFeeds(response.data);
    } catch (error) {
      console.error('Error loading saved feeds:', error);
      toast.error('Failed to load saved feeds');
    } finally {
      setLoading(false);
    }
  };

  const handleRemoveSavedFeed = async (savedFeedId: number) => {
    if (window.confirm('Are you sure you want to remove this saved feed?')) {
      try {
        await apiService.removeSavedFeed(savedFeedId);
        toast.success('Feed removed from saved list');
        loadSavedFeeds();
      } catch (error) {
        console.error('Error removing saved feed:', error);
        toast.error('Failed to remove saved feed');
      }
    }
  };

  const handleUpdateNotes = async () => {
    if (!editingNotes) return;
    
    try {
      await apiService.updateSavedFeedNotes(editingNotes.id, editingNotes.notes);
      toast.success('Notes updated successfully');
      setEditingNotes(null);
      loadSavedFeeds();
    } catch (error) {
      console.error('Error updating notes:', error);
      toast.error('Failed to update notes');
    }
  };

  const formatTimeAgo = (dateString: string) => {
    const date = new Date(dateString);
    const now = new Date();
    const diffInHours = Math.floor((now.getTime() - date.getTime()) / (1000 * 60 * 60));
    
    if (diffInHours < 1) return 'Just now';
    if (diffInHours < 24) return `${diffInHours} hour${diffInHours > 1 ? 's' : ''} ago`;
    
    const diffInDays = Math.floor(diffInHours / 24);
    if (diffInDays < 7) return `${diffInDays} day${diffInDays > 1 ? 's' : ''} ago`;
    
    const diffInWeeks = Math.floor(diffInDays / 7);
    if (diffInWeeks < 4) return `${diffInWeeks} week${diffInWeeks > 1 ? 's' : ''} ago`;
    
    return date.toLocaleDateString('en-US', { month: 'short', day: 'numeric' });
  };

  const getProficiencyDescription = (level: number) => {
    switch (level) {
      case 5: return 'Expert - Master level with extensive experience';
      case 4: return 'Advanced - Highly skilled with deep knowledge';
      case 3: return 'Intermediate - Solid foundation with practical experience';
      case 2: return 'Beginner - Basic understanding, learning actively';
      case 1: return 'Novice - Just starting, eager to learn';
      default: return 'Level not specified';
    }
  };

  return (
    <div className="min-h-screen bg-gradient-to-br from-blue-50 via-white to-teal-50">
      {/* Header */}
      <div className="bg-white shadow-lg border-b border-blue-100">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex items-center justify-between h-16">
            <div className="flex items-center space-x-4">
              <div className="flex items-center space-x-2 group">
                <div className="h-10 w-10 bg-gradient-to-br from-blue-600 to-teal-600 rounded-xl flex items-center justify-center shadow-lg group-hover:shadow-xl transform group-hover:scale-110 transition-all duration-300">
                  <Bookmark className="h-6 w-6 text-white" />
                </div>
                <h1 className="text-2xl font-bold gradient-text group-hover:scale-105 transition-transform duration-300">LearnLoop</h1>
              </div>
            </div>
            
            {/* Search Bar */}
            <div className="flex-1 max-w-md mx-8">
              <div className="relative group">
                <input
                  type="text"
                  placeholder="Search saved feeds..."
                  value={searchQuery}
                  onChange={(e) => setSearchQuery(e.target.value)}
                  className="w-full pl-10 pr-4 py-3 bg-gray-50 border border-gray-200 rounded-xl focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent focus:bg-white transition-all duration-300 hover:border-blue-300 focus:scale-[1.02] shadow-sm hover:shadow-md"
                />
                <Search className="absolute left-3 top-3 h-5 w-5 text-gray-400 group-focus-within:text-blue-500 transition-colors" />
              </div>
            </div>
          </div>
        </div>
      </div>

      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        {/* Page Header */}
        <div className="mb-8">
          <h2 className="text-4xl font-bold gradient-text mb-3">Saved Feeds</h2>
          <p className="text-gray-600 text-lg">Your collection of interesting posts and skills</p>
        </div>

        {/* Saved Feeds Grid */}
        {loading ? (
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
            {[1, 2, 3, 4, 5, 6].map((i) => (
              <div key={i} className="bg-white rounded-xl shadow-lg border border-blue-100 p-6 animate-pulse hover:shadow-xl transition-all duration-300">
                <div className="h-4 bg-gray-200 rounded mb-3"></div>
                <div className="h-3 bg-gray-200 rounded mb-2"></div>
                <div className="h-3 bg-gray-200 rounded w-3/4 mb-4"></div>
                <div className="h-8 bg-gray-200 rounded mb-3"></div>
                <div className="h-3 bg-gray-200 rounded w-1/2"></div>
              </div>
            ))}
          </div>
        ) : savedFeeds.length === 0 ? (
          <div className="text-center py-16">
            <div className="mx-auto h-20 w-20 bg-gradient-to-r from-blue-100 to-teal-100 rounded-full flex items-center justify-center mb-6 shadow-lg">
              <Bookmark className="h-10 w-10 text-blue-600" />
            </div>
            <h3 className="text-xl font-semibold text-gray-900 mb-3">No saved feeds yet</h3>
            <p className="text-gray-600 mb-6 text-lg">Start saving interesting posts from the Feed page</p>
            <a
              href="/feed"
              className="inline-flex items-center px-8 py-4 bg-gradient-to-r from-blue-600 to-teal-600 text-white rounded-xl hover:from-blue-700 hover:to-teal-700 transition-all duration-300 transform hover:scale-105 active:scale-95 shadow-lg hover:shadow-xl font-semibold text-lg"
            >
              <Plus className="h-5 w-5 mr-2" />
              Browse Feed
            </a>
          </div>
        ) : (
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
            {savedFeeds.map((savedFeed) => (
              <div key={savedFeed.id} className="bg-white rounded-xl shadow-lg border border-blue-100 overflow-hidden hover:shadow-xl transition-all duration-300 hover:scale-[1.02]">
                {/* Header */}
                <div className="p-6 border-b border-blue-100">
                  <div className="flex items-start justify-between">
                    <div className="flex items-center space-x-3">
                      <div className="h-12 w-12 bg-gradient-to-r from-blue-600 to-teal-600 rounded-full flex items-center justify-center shadow-lg">
                        <span className="text-white font-semibold text-lg">
                          {savedFeed.teacherUsername.charAt(0).toUpperCase()}
                        </span>
                      </div>
                      <div className="flex-1">
                        <h3 className="text-lg font-semibold text-gray-900">
                          {savedFeed.teacherName || savedFeed.teacherUsername}
                        </h3>
                        <p className="text-sm text-gray-600 mb-1">
                          {savedFeed.skillName} • {savedFeed.relationType === 'TEACH' ? 'Teaching' : 'Learning'}
                        </p>
                        <p className="text-xs text-gray-500">
                          @{savedFeed.teacherUsername}
                        </p>
                      </div>
                    </div>
                    <div className="flex items-center space-x-2">
                      <span className={`px-3 py-1 rounded-full text-xs font-medium ${
                        savedFeed.relationType === 'TEACH' 
                          ? 'bg-green-100 text-green-800' 
                          : 'bg-blue-100 text-blue-800'
                      }`}>
                        {savedFeed.relationType === 'TEACH' ? 'Teacher' : 'Learner'}
                      </span>
                    </div>
                  </div>
                </div>

                {/* Content */}
                <div className="p-6">
                  {/* Skill Information */}
                  <div className="mb-4">
                    <h4 className="text-lg font-semibold text-gray-900 mb-2">
                      {savedFeed.skillName}
                    </h4>
                    {savedFeed.skillDescription && (
                      <p className="text-gray-600 mb-3 leading-relaxed">
                        {savedFeed.skillDescription}
                      </p>
                    )}
                    <div className="flex items-center space-x-2 mb-3">
                      <span className="px-3 py-1 bg-blue-100 text-blue-800 rounded-full text-xs font-medium">
                        {savedFeed.skillCategory}
                      </span>
                    </div>
                  </div>

                  {/* Proficiency and Details */}
                  <div className="grid gap-3 mb-4">
                    <div className="flex items-center space-x-2 p-3 bg-yellow-50 rounded-lg border border-yellow-100">
                      <Star className="h-4 w-4 text-yellow-600" />
                      <div>
                        <span className="text-sm font-medium text-yellow-800">
                          Level {savedFeed.proficiencyLevel}/5
                        </span>
                        <p className="text-xs text-yellow-700">
                          {getProficiencyDescription(savedFeed.proficiencyLevel)}
                        </p>
                      </div>
                    </div>
                    
                    {savedFeed.hourlyRate && savedFeed.hourlyRate > 0 && (
                      <div className="flex items-center space-x-2 p-3 bg-green-50 rounded-lg border border-green-100">
                        <Users className="h-4 w-4 text-green-600" />
                        <div>
                          <span className="text-sm font-medium text-green-800">
                            {savedFeed.hourlyRate} Credits per session
                          </span>
                          <p className="text-xs text-green-700">Session Pricing</p>
                        </div>
                      </div>
                    )}
                    
                    {savedFeed.availability && savedFeed.availability.trim() !== '' && (
                      <div className="flex items-center space-x-2 p-3 bg-blue-50 rounded-lg border border-blue-100">
                        <Calendar className="h-4 w-4 text-blue-600" />
                        <div>
                          <span className="text-sm font-medium text-blue-800">
                            Available: {savedFeed.availability}
                          </span>
                          <p className="text-xs text-blue-700">Schedule</p>
                        </div>
                      </div>
                    )}
                  </div>

                  {/* User Notes */}
                  {savedFeed.notes && (
                    <div className="mb-4 p-3 bg-gray-50 rounded-lg border border-gray-100">
                      <div className="flex items-start justify-between">
                        <div className="flex-1">
                          <h5 className="text-sm font-medium text-gray-900 mb-1">Your Notes</h5>
                          <p className="text-sm text-gray-700">{savedFeed.notes}</p>
                        </div>
                        <button
                          onClick={() => setEditingNotes({ id: savedFeed.id, notes: savedFeed.notes })}
                          className="p-1 text-gray-400 hover:text-gray-600 hover:bg-gray-200 rounded transition-colors ml-2"
                        >
                          <Edit className="h-3 w-3" />
                        </button>
                      </div>
                    </div>
                  )}

                  {/* Action Buttons */}
                  <div className="flex space-x-3">
                    <button
                      onClick={() => window.open(`/feed`, '_blank')}
                      className="flex-1 py-2.5 px-4 bg-gradient-to-r from-blue-500 to-purple-600 text-white rounded-lg hover:from-blue-600 hover:to-purple-700 transition-all duration-200 font-medium"
                    >
                      <MessageCircle className="h-4 w-4 mr-2 inline" />
                      View Original
                    </button>
                    <button
                      onClick={() => handleRemoveSavedFeed(savedFeed.id)}
                      className="px-4 py-2.5 text-red-600 border border-red-200 rounded-lg hover:bg-red-50 transition-colors"
                      title="Remove from saved"
                    >
                      <Trash2 className="h-4 w-4" />
                    </button>
                  </div>
                </div>

                {/* Footer */}
                <div className="px-6 py-3 bg-gray-50 border-t border-gray-100">
                  <div className="flex items-center justify-between text-xs text-gray-500">
                    <span className="flex items-center">
                      <Bookmark className="h-3 w-3 mr-1" />
                      Saved {formatTimeAgo(savedFeed.savedAt)}
                    </span>
                  </div>
                </div>
              </div>
            ))}
          </div>
        )}
      </div>

      {/* Edit Notes Modal */}
      {editingNotes && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center p-4 z-50">
          <div className="bg-white rounded-2xl max-w-md w-full p-6 shadow-2xl">
            <div className="flex items-center justify-between mb-4">
              <h3 className="text-lg font-semibold text-gray-900">Edit Notes</h3>
              <button
                onClick={() => setEditingNotes(null)}
                className="p-1 text-gray-400 hover:text-gray-600 hover:bg-gray-100 rounded-full transition-colors"
              >
                <X className="h-5 w-5" />
              </button>
            </div>
            
            <div className="mb-4">
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Your Notes
              </label>
              <textarea
                value={editingNotes.notes}
                onChange={(e) => setEditingNotes({ ...editingNotes, notes: e.target.value })}
                rows={4}
                className="w-full px-3 py-2 border border-gray-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                placeholder="Add your notes about this saved feed..."
              />
            </div>

            <div className="flex items-center justify-end space-x-3">
              <button
                onClick={() => setEditingNotes(null)}
                className="px-4 py-2 text-gray-700 border border-gray-300 rounded-lg hover:bg-gray-50 transition-colors"
              >
                Cancel
              </button>
              <button
                onClick={handleUpdateNotes}
                className="px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors"
              >
                Update Notes
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default SavedFeeds;
